"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { Check, Copy } from "lucide-react";
import { Button } from "@/src/components";

export interface CodeBlockProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Code to display */
  code: string;
  /** Language for syntax highlighting context */
  language?: "tsx" | "bash" | "css" | "json";
  /** Show line numbers */
  showLineNumbers?: boolean;
  /** Filename to display */
  filename?: string;
  /** Allow copying */
  copyable?: boolean;
}

export function CodeBlock({
  code,
  language = "tsx",
  showLineNumbers = false,
  filename,
  copyable = true,
  className,
  ...props
}: CodeBlockProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lines = code.trim().split("\n");

  return (
    <div
      className={cn(
        "relative rounded-lg border bg-muted/50 overflow-hidden",
        className
      )}
      {...props}
    >
      {/* Header with filename and copy button */}
      {(filename || copyable) && (
        <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
          {filename && (
            <div className="text-xs font-medium text-muted-foreground">
              {filename}
            </div>
          )}
          {!filename && <div />}
          {copyable && (
            <Button
              variant="text"
              size="icon"
              className="h-7 w-7"
              onClick={handleCopy}
              aria-label={copied ? "Copied" : "Copy code"}
            >
              {copied ? (
                <Check className="icon-sm text-success" />
              ) : (
                <Copy className="icon-sm" />
              )}
            </Button>
          )}
        </div>
      )}

      {/* Code content */}
      <div className="overflow-x-auto p-4">
        <pre className="font-mono text-sm">
          <code>
            {showLineNumbers
              ? lines.map((line, i) => (
                  <div key={i} className="flex">
                    <div className="select-none text-muted-foreground w-8 shrink-0 text-right pr-4">
                      {i + 1}
                    </div>
                    <div>{line}</div>
                  </div>
                ))
              : code.trim()}
          </code>
        </pre>
      </div>
    </div>
  );
}

/**
 * Inline code snippet with copy functionality
 */
export function InlineCode({
  children,
  copyable = false,
  className,
}: {
  children: string;
  copyable?: boolean;
  className?: string;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <code
      className={cn(
        "relative rounded bg-muted px-1.5 py-0.5 font-mono text-sm",
        copyable && "pr-7 cursor-pointer hover:bg-muted/80",
        className
      )}
      onClick={copyable ? handleCopy : undefined}
    >
      {children}
      {copyable && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          {copied ? (
            <Check className="h-3 w-3 text-success" />
          ) : (
            <Copy className="h-3 w-3 text-muted-foreground" />
          )}
        </div>
      )}
    </code>
  );
}

export default CodeBlock;
